import get from "lodash.get";
import React from "react";
import { createClasses } from "@bloomberg/media-ui-core";
import styles from "./styles.module.css";
import {
  formatPercentChange,
  formatPriceChange,
  isTickerUp,
} from "~utils/portfolio/tickerUtils";
import { formatWithCommasAndDecimals } from "~utils/portfolio/formatNumber";
import { ENTRY_DATA_FORMAT } from "~constants/Portfolio";
import { PortfolioColumnConfig, PortfolioLot } from "~types/portfolio";
import timeFormat from "~utils/timeFormat";
import { Locale } from "~constants/i18n";

const MM_DD_YYYY = "MM/dd/yyyy";

type LotsEntryProps = PortfolioColumnConfig & {
  lot: PortfolioLot;
};

const getValueFromLot = (
  lot: PortfolioLot,
  lodashPath: string
): string | number => {
  if (!lodashPath) {
    return "—";
  }

  return get(lot, lodashPath, "—");
};

const chooseColor = (change: number) => {
  return change >= 0 ? styles.positive : styles.negative;
};

const formatData = (
  data: string | number,
  formatType: string,
  securityType: string,
  columnName: string
) => {
  const { PRICE_CHANGE, PERCENT_CHANGE, COMMAS_AND_DECIMALS } =
    ENTRY_DATA_FORMAT;

  if (data === "—" || typeof data === "string") {
    return data;
  }

  switch (formatType) {
    case PRICE_CHANGE:
      if (columnName === "1D GAIN") {
        return formatPriceChange(data, securityType);
      }

      return formatPriceChange(data);
    case PERCENT_CHANGE:
      return formatPercentChange(data);
    case COMMAS_AND_DECIMALS:
      return formatWithCommasAndDecimals(data);
    default:
      return data;
  }
};

const generateLotConfig = (
  lot: PortfolioLot,
  lotLodashPath: string,
  lotDataFormat: string,
  columnName: string
) => {
  const lotConfig = {
    lotInformation: getValueFromLot(lot, lotLodashPath),
    securityType: getValueFromLot(lot, "SECTYPE"),
    totalGainLoss: 0,
    shares: 0,
    performanceIndicator: "",
  };

  // A one-off market bar indicator
  if (columnName === "SYMBOL") {
    lotConfig.lotInformation = timeFormat(
      lotConfig.lotInformation,
      Locale.En,
      MM_DD_YYYY
    );
    lotConfig.totalGainLoss = getValueFromLot(
      lot,
      "marketValue.totalGainLoss.value"
    ) as number;
    lotConfig.shares = getValueFromLot(lot, "shares.number") as number;
  }

  // A one-off for modifying background colors of the Gain columns to indicate performance
  if (columnName === "TOTAL GAIN" || columnName === "1D GAIN") {
    lotConfig.performanceIndicator = chooseColor(
      lotConfig.lotInformation as number
    );
    lotConfig.shares = getValueFromLot(lot, "shares.number") as number;
  }

  lotConfig.lotInformation = formatData(
    lotConfig.lotInformation,
    lotDataFormat,
    lotConfig.securityType as string,
    columnName
  );

  return lotConfig;
};

export default function LotsEntry(props: LotsEntryProps) {
  const {
    alignment,
    lot,
    columnName,
    lotLodashPath,
    lotDataFormat,
    lotAriaLabel,
  } = props;
  const { totalGainLoss, lotInformation, performanceIndicator, shares } =
    generateLotConfig(lot, lotLodashPath, lotDataFormat, columnName);
  const marketPerformance = isTickerUp(totalGainLoss)
    ? styles.marketsUp
    : styles.marketsDown;
  const totalPerformance = shares ? marketPerformance : "";
  const rightAlignment = alignment === "right" ? styles.right : "";
  const ariaLabel = lotInformation === "—" ? "No data" : lotAriaLabel;
  const gainColor =
    lotInformation === "—" || shares === 0 ? "" : performanceIndicator;

  return (
    <div
      className={createClasses([styles.lotEntry, totalPerformance])}
      role="cell"
    >
      <div className={styles.lotEntryWrapper}>
        <div
          className={createClasses([
            styles.lotInformation,
            gainColor,
            rightAlignment,
          ])}
          aria-label={ariaLabel}
        >
          {lotInformation}
        </div>
      </div>
    </div>
  );
}
